import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API_BASE_URL } from "./config";

const Home = () => {
  const [activeTab, setActiveTab] = useState("benefits");
  const [benefits, setBenefits] = useState([]);
  const [loadingBenefits, setLoadingBenefits] = useState(true);
  const [locals, setLocals] = useState([]);
  const [loadingLocals, setLoadingLocals] = useState(true);
  const [userLocation, setUserLocation] = useState({ city: "", zipCode: "" });

  useEffect(() => {
    fetchBenefits();
    fetchLocals();
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE_URL}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserLocation({ 
        city: res.data.city || "", 
        zipCode: res.data.zipCode || "" 
      });
    } catch (err) {
      console.error("Error fetching user profile:", err);
    }
  };

  const fetchBenefits = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/benefits`);
      setBenefits(res.data);
      setLoadingBenefits(false);
    } catch (err) {
      console.error("Error fetching benefits:", err);
      setLoadingBenefits(false);
    }
  };

  const fetchLocals = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE_URL}/api/locals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocals(res.data);
      setLoadingLocals(false);
    } catch (err) {
      console.error("Error fetching locals:", err);
      setLoadingLocals(false);
    }
  };

  const renderFeedContent = () => {
    switch (activeTab) {
      case "benefits":
        return (
          <div className="feed-content">
            <h3>Benefits Feed</h3>
            {loadingBenefits ? (
              <div className="text-center py-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : benefits.length === 0 ? (
              <div className="alert alert-info">
                No benefits available at the moment. Check back later!
              </div>
            ) : (
              benefits.map((benefit) => (
                <div className="card mb-3" key={benefit._id}>
                  {benefit.image && (
                    <img 
                      src={`${API_BASE_URL}/${benefit.image}`}
                      className="card-img-top"
                      alt={benefit.name}
                      style={{
                        width: "100%",
                        height: "auto",
                        maxHeight: "400px",
                        objectFit: "cover"
                      }}
                    />
                  )}
                  <div className="card-body">
                    <h5 className="card-title">{benefit.name}</h5>
                    <p className="card-text">{benefit.description}</p>
                    
                    {benefit.bulletPoints && benefit.bulletPoints.length > 0 && (
                      <ul className="mb-3">
                        {benefit.bulletPoints.map((point, index) => (
                          <li key={index}>{point}</li>
                        ))}
                      </ul>
                    )}
                    
                    <div className="d-flex justify-content-between align-items-center">
                      <a 
                        href={benefit.accessUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary"
                      >
                        Access Benefit
                      </a>
                      <small className="text-muted">
                        ID: {benefit.benefitId}
                      </small>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        );
      case "locals":
        return (
          <div className="feed-content">
            <h3>Local Community Members</h3>
            {userLocation.city || userLocation.zipCode ? (
              <p className="text-muted mb-3">
                Showing members in {userLocation.city || ""} {userLocation.zipCode ? `(${userLocation.zipCode})` : ""}
              </p>
            ) : (
              <div className="alert alert-info mb-3">
                <strong>Tip:</strong> Update your profile with your city and zip code to see local members in your area.
              </div>
            )}
            
            {loadingLocals ? (
              <div className="text-center py-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : locals.length === 0 ? (
              <div className="alert alert-info">
                {userLocation.city || userLocation.zipCode 
                  ? "No other members found in your area yet. Invite friends to join!"
                  : "Add your location to your profile to connect with local members."}
              </div>
            ) : (
              locals.map((local) => (
                <div className="card mb-3" key={local._id}>
                  <div className="card-body">
                    <div className="d-flex align-items-start">
                      {local.profilePic && (
                        <img 
                          src={`${API_BASE_URL}/${local.profilePic}`}
                          alt={local.username}
                          className="rounded-circle me-3"
                          style={{ 
                            width: "60px", 
                            height: "60px", 
                            objectFit: "cover" 
                          }}
                        />
                      )}
                      <div className="flex-grow-1">
                        <h5 className="card-title mb-1">
                          {local.displayUsername ? (
                            <Link 
                              to={`/user/${local.displayUsername}`}
                              className="text-decoration-none text-dark"
                            >
                              {local.fullName || local.username.split('@')[0]}
                              <span className="text-muted small ms-2">@{local.displayUsername}</span>
                            </Link>
                          ) : (
                            <>
                              {local.fullName || local.username.split('@')[0]}
                            </>
                          )}
                        </h5>
                        {local.age && (
                          <p className="text-muted small mb-1">{local.age} years old</p>
                        )}
                        {local.tagline && (
                          <p className="text-muted small mb-2">{local.tagline}</p>
                        )}
                        {local.bio && (
                          <p className="card-text">{local.bio}</p>
                        )}
                        <div className="d-flex justify-content-between align-items-center">
                          <small className="text-muted">
                            {local.city && local.zipCode 
                              ? `${local.city}, ${local.zipCode}`
                              : local.city || local.zipCode || "Location not specified"}
                          </small>
                          {local.displayUsername && (
                            <Link 
                              to={`/user/${local.displayUsername}`}
                              className="btn btn-sm btn-outline-primary"
                            >
                              View Profile
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        );
      case "news":
        return (
          <div className="feed-content">
            <h3>News & Updates</h3>
            <div className="card mb-3">
              <div className="card-body">
                <h5 className="card-title">Health Alert: Flu Season</h5>
                <p className="card-text">CDC recommends flu vaccines for seniors. Free vaccines available at local pharmacies.</p>
                <small className="text-muted">1 hour ago</small>
              </div>
            </div>
            <div className="card mb-3">
              <div className="card-body">
                <h5 className="card-title">Technology Workshop</h5>
                <p className="card-text">Free smartphone and tablet training sessions for seniors at the library this week.</p>
                <small className="text-muted">4 hours ago</small>
              </div>
            </div>
            <div className="card mb-3">
              <div className="card-body">
                <h5 className="card-title">Community Safety Update</h5>
                <p className="card-text">Local police department hosting senior safety workshop on avoiding scams.</p>
                <small className="text-muted">2 days ago</small>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="container mt-4">
      <div className="row justify-content-center">
        <div className="col-md-8">
          <div className="card">
            <div className="card-header">
              <ul className="nav nav-tabs card-header-tabs">
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === "benefits" ? "active" : ""}`}
                    onClick={() => setActiveTab("benefits")}
                    style={{ cursor: "pointer", border: "none", background: "none" }}
                  >
                    Benefits
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === "locals" ? "active" : ""}`}
                    onClick={() => setActiveTab("locals")}
                    style={{ cursor: "pointer", border: "none", background: "none" }}
                  >
                    Locals
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === "news" ? "active" : ""}`}
                    onClick={() => setActiveTab("news")}
                    style={{ cursor: "pointer", border: "none", background: "none" }}
                  >
                    News
                  </button>
                </li>
              </ul>
            </div>
            <div className="card-body">
              {renderFeedContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;